import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker, { isValidHexColor, normalizeHexColor, saveCustomColor } from "../ColorPicker";
import { IconButton, InputAdornment } from "@material-ui/core";
import { Colorize } from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		width: "100%",
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(0.5),
	},
	colorAdorment: {
		width: 20,
		height: 20,
	},
}));

const QueueSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muy corto")
		.max(50, "Muy largo")
		.required("Requerido"),
	color: Yup.string().min(3, "Muy corto").max(9, "Muy largo").required("Requerido"),
	greetingMessage: Yup.string(),
	sharedConnectionIds: Yup.array().min(1, "Requerido"),
});

const normalizeConnectionIds = (...values) => {
	const ids = new Set();
	values.forEach(value => {
		if (!value) return;
		if (Array.isArray(value)) {
			value.forEach(id => {
				if (id) ids.add(Number(id));
			});
			return;
		}
		ids.add(Number(value));
	});
	return Array.from(ids).filter(id => !Number.isNaN(id));
};

const getWhatsappName = (connections, connectionId) =>
	connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.name ||
	connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.number ||
	`Conexión ${connectionId}`;

const QueueModal = ({ open, onClose, queueId, defaultConnectionId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		color: "#2f80ed",
		greetingMessage: "",
		connectionId: "",
		sharedConnectionIds: [],
	};

	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [queue, setQueue] = useState(initialState);
	const [connections, setConnections] = useState([]);
	const greetingRef = useRef();

	useEffect(() => {
		(async () => {
			try {
				const { data: connectionData } = await api.get("/connections/");
				setConnections(connectionData || []);
			} catch (err) {
				toastError(err);
			}

			if (!queueId) {
				const sharedConnectionIds = normalizeConnectionIds(defaultConnectionId);
				setQueue({
					...initialState,
					connectionId: defaultConnectionId || "",
					sharedConnectionIds,
				});
				return;
			}

			try {
				const { data } = await api.get(`/queue/${queueId}`);
				setQueue(prevState => {
					return {
						...prevState,
						...data,
						connectionId: data.connectionId || data.connection?.id || defaultConnectionId || "",
						sharedConnectionIds: normalizeConnectionIds(
							data.sharedConnectionIds,
							data.connectionId || data.connection?.id,
							defaultConnectionId
						),
					};
				});
			} catch (err) {
				toastError(err);
			}
		})();

		return () => {
			setQueue({
				name: "",
				color: "#2f80ed",
				greetingMessage: "",
				connectionId: "",
				sharedConnectionIds: [],
			});
		};
	}, [queueId, open, defaultConnectionId]);

	const handleClose = () => {
		onClose();
		setQueue(initialState);
	};

	const handleSaveQueue = async values => {
		try {
			if (isValidHexColor(values.color)) {
				saveCustomColor(values.color);
			}
			const sharedConnectionIds = normalizeConnectionIds(
				values.sharedConnectionIds,
				values.connectionId
			);
			const payload = {
				...values,
				connectionId: values.connectionId || sharedConnectionIds[0] || "",
				sharedConnectionIds,
				color: normalizeHexColor(values.color),
			};
			if (queueId) {
				await api.put(`/queue/${queueId}`, payload);
			} else {
				await api.post("/queue", payload);
			}
			toast.success(i18n.t("queueModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} scroll="paper">
				<DialogTitle>
					{queueId
						? `${i18n.t("queueModal.title.edit")}`
						: `${i18n.t("queueModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={queue}
					enableReinitialize={true}
					validationSchema={QueueSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveQueue(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue, handleBlur }) => (
						<Form>
							<DialogContent dividers>
								<Field
									as={TextField}
									label={i18n.t("queueModal.form.name")}
									autoFocus
									name="name"
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<FormControl variant="outlined" className={classes.formControl}>
									<InputLabel>Conexiones</InputLabel>
									<Select
										multiple
										value={values.sharedConnectionIds || []}
										label="Conexiones"
										renderValue={selected =>
											selected.map(id => getWhatsappName(connections, id)).join(", ")
										}
										onChange={event => {
											const selectedIds = event.target.value;
											setFieldValue("sharedConnectionIds", selectedIds);
											setFieldValue(
												"connectionId",
												selectedIds.includes(Number(values.connectionId))
													? values.connectionId
													: selectedIds[0] || ""
											);
										}}
									>
										{connections.map(whatsapp => (
											<MenuItem key={whatsapp.id} value={whatsapp.id}>
												<Checkbox checked={(values.sharedConnectionIds || []).indexOf(whatsapp.id) > -1} />
												<ListItemText
													primary={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
												/>
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<Field
									as={TextField}
									label={i18n.t("queueModal.form.color")}
									name="color"
									id="color"
									onBlur={event => {
										handleBlur(event);
										if (isValidHexColor(event.target.value)) {
											const color = normalizeHexColor(event.target.value);
											setFieldValue("color", color);
											saveCustomColor(color);
										}
									}}
									error={touched.color && Boolean(errors.color)}
									helperText={touched.color && errors.color}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<div
													style={{ backgroundColor: values.color }}
													className={classes.colorAdorment}
												></div>
											</InputAdornment>
										),
										endAdornment: (
											<IconButton
												size="small"
												color="default"
												onClick={() => setColorPickerModalOpen(true)}
											>
												<Colorize />
											</IconButton>
										),
									}}
									variant="outlined"
									margin="dense"
								/>
								<ColorPicker
									open={colorPickerModalOpen}
									currentColor={values.color}
									handleClose={() => setColorPickerModalOpen(false)}
									onChange={color => {
										setFieldValue("color", color);
										setQueue(() => {
											return { ...values, color };
										});
									}}
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("queueModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										inputRef={greetingRef}
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("queueModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{queueId
										? `${i18n.t("queueModal.buttons.okEdit")}`
										: `${i18n.t("queueModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default QueueModal;

